import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../services/api";
import { API_ENDPOINTS } from "../constants/actionTypes";

// Thunk for fetching ALL users
export const fetchAllCompanyUsers = createAsyncThunk(
  "companyUser/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      // FIXED: Clean GET request - NO query params (was causing backend failure)
      const response = await axiosInstance.get(
        API_ENDPOINTS.COMPANY_USER.SELECT_ALL,
      );

      return response.data;
    } catch (error) {
      console.error(
        "❌ CompanyUsers API Error:",
        error.response?.status,
        error.response?.data,
      );
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch company users",
      );
    }
  },
);

// Existing thunk for ID search
export const fetchCompanyUserById = createAsyncThunk(
  "companyUser/fetchById",
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${API_ENDPOINTS.COMPANY_USER.SELECT_BY_ID}/${userId}`,
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "User not found");
    }
  },
);
// Add these thunks to src/features/companyUser/companyUserSlice.js

export const addCompanyUser = createAsyncThunk(
  "companyUser/add",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.COMPANY_USER.INSERT,
        userData,
      );
      dispatch(fetchAllCompanyUsers());
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to add user",
      );
    }
  },
);

export const updateCompanyUser = createAsyncThunk(
  "companyUser/update",
  async (userData, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance.put(
        API_ENDPOINTS.COMPANY_USER.UPDATE,
        userData,
      );
      dispatch(fetchAllCompanyUsers()); // Refresh list after update
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user",
      );
    }
  },
);

export const deleteCompanyUser = createAsyncThunk(
  "companyUser/delete",
  async (userId, { rejectWithValue, dispatch }) => {
    try {
      // Use query parameter if the backend requires it
      await axiosInstance.delete(
        `${API_ENDPOINTS.COMPANY_USER.DELETE}?id=${userId}`,
      );
      dispatch(fetchAllCompanyUsers());
      return userId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user",
      );
    }
  },
);

const companyUserSlice = createSlice({
  name: "companyUser",
  initialState: {
    usersList: [], // Flat user array for table
    pagination: null, // Full pagination wrapper {totalCount, data: [...]}
    selectedUser: null,
    isLoading: false,
    error: null,
  },
  reducers: {
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All Users - FIXED: Clean GET, added pagination
      .addCase(fetchAllCompanyUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCompanyUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        const apiData = action.payload?.data;
        state.usersList = apiData?.data || [];
        state.pagination = apiData;
      })
      .addCase(fetchAllCompanyUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch company users";
      })
      // Fetch By ID
      .addCase(fetchCompanyUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCompanyUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload.data;
      })
      .addCase(fetchCompanyUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Add Company User
      .addCase(addCompanyUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addCompanyUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(addCompanyUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Company User
      .addCase(updateCompanyUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateCompanyUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateCompanyUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete Company User
      .addCase(deleteCompanyUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteCompanyUser.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteCompanyUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearSelectedUser } = companyUserSlice.actions;
export default companyUserSlice.reducer;
